import { KK } from '@/define/define'
import { speciaBehiver, specialCheck, specialCheckCurrent } from '@/define/limit'
import { getCurrentDevice } from '@/profiles/selector'
import { DeviceManager } from '@/service/device'
import { FnLayerCapability } from '@/service/fn_layer_capability'
import { SetType } from '@/service/driver/action_set'
import { MagnetSeniorType } from '@libs/types/enum'
import { setData } from '@/service/driver/data'

export interface ActionVisibilityState {
  actionMessage: string
  isShowNormal: boolean
  isShowMacro: boolean
  isShowMagnet: boolean
  isSupportKME: boolean
}

export interface ActionVisibilityContext {
  kk: KK
  isFn: boolean
  data: ReturnType<typeof setData.getData>
  supportKME: boolean
  t: (key: string) => string
}

type DeviceVisibilityRule = {
  match: () => boolean
  handler: (context: ActionVisibilityContext, state: ActionVisibilityState) => void
  exclusive?: boolean
}

export const resolveActionVisibility = (context: ActionVisibilityContext): ActionVisibilityState => {
  const state: ActionVisibilityState = {
    actionMessage: '',
    isShowNormal: true,
    isShowMacro: true,
    isShowMagnet: getCurrentDevice.value.magneticAxis,
    isSupportKME: context.supportKME,
  }

  applyLayerConflictRules(context, state)
  applyDeviceVisibilityRules(context, state)

  return state
}

export const isActionSetTypeVisible = (current: SetType, state: ActionVisibilityState): boolean => {
  if (current === SetType.routine) {
    return state.isShowNormal
  }
  if (current === SetType.macro) {
    return state.isShowMacro
  }

  const advancedTypes = [SetType.dks, SetType.mpt, SetType.end, SetType.rs, SetType.mt, SetType.tgl]
  if (advancedTypes.includes(current)) {
    return state.isShowMagnet
  }

  if (current === SetType.kme) {
    return state.isSupportKME
  }

  return true
}

const isFactoryFnKey = (kk: KK): boolean => {
  const fnFactoryKeys = getCurrentDevice.value.fnFactoryKeys
  return fnFactoryKeys.length > 0 && fnFactoryKeys.includes(kk)
}

const applyLayerConflictRules = (context: ActionVisibilityContext, state: ActionVisibilityState) => {
  const { kk, isFn, data, t } = context

  const hasNormalMagnet = kk >= 0 && data.Layout.Magenet && data.Layout.Magenet[kk] && data.Layout.Magenet[kk].KeyType !== MagnetSeniorType.SeniorKeyTypeNone
  const hasNormalFire = kk >= 0 && data.Layout.KeyMapFire && data.Layout.KeyMapFire[kk] && data.Layout.KeyMapFire[kk].length > 0
  const isNormalAdvanced = Boolean(hasNormalMagnet || hasNormalFire)

  const isFnBasic =
    kk >= 0 &&
    ((data.LayoutFn.KeyMap && data.LayoutFn.KeyMap[kk] > 0) ||
      (data.LayoutFn.KeyMapModifier && data.LayoutFn.KeyMapModifier[kk] && data.LayoutFn.KeyMapModifier[kk].length > 0) ||
      (data.LayoutFn.Macros && data.LayoutFn.Macros[kk]) ||
      (data.LayoutFn.MacroType && data.LayoutFn.MacroType[kk]))

  const factoryFn = isFactoryFnKey(kk)

  if (isFn) {
    if (!FnLayerCapability.isEditable()) {
      state.isShowNormal = false
      state.isShowMacro = false
      state.isShowMagnet = false
      state.isSupportKME = false
      state.actionMessage = t('fn_layer_edit_unavailable_tip')
      return
    }

    state.isShowMagnet = false
    state.isSupportKME = false

    if (isNormalAdvanced && !factoryFn) {
      state.isShowNormal = false
      state.isShowMacro = false
      state.actionMessage = t('set_type_message')
    }
    return
  }

  if (isFnBasic && !factoryFn) {
    state.isShowMagnet = false
    state.isSupportKME = false
    state.actionMessage = t('set_type_message_fn')
  }
}

const deviceVisibilityRules: DeviceVisibilityRule[] = [
  {
    match: () => specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V1),
    handler: handleJingtaiV1Device,
    exclusive: true,
  },
  {
    match: () => specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V2),
    handler: handleJingtaiV2Device,
    exclusive: true,
  },
  {
    match: () => specialCheck(speciaBehiver.DeviceOfRoyuan, DeviceManager.instance.state.currentPid),
    handler: handleRoYuanDevice,
  },
  {
    match: () => specialCheckCurrent(speciaBehiver.DevicesOfSelfDev) || specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMix) || specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMagnet),
    handler: handleDevicesOfSelfDev,
  },
]

const applyDeviceVisibilityRules = (context: ActionVisibilityContext, state: ActionVisibilityState) => {
  for (const rule of deviceVisibilityRules) {
    if (!rule.match()) {
      continue
    }

    rule.handler(context, state)
    if (rule.exclusive) {
      break
    }
  }
}

function handleJingtaiV1Device({ kk, isFn, data, t }: ActionVisibilityContext, state: ActionVisibilityState) {
  const factoryFn = isFactoryFnKey(kk)
  if (isFn) {
    const layout = data.Layout
    state.isShowMagnet = false
    state.isSupportKME = false

    if ((layout.Macros[kk] || layout.Magenet[kk]) && !factoryFn) {
      state.isShowNormal = false
      state.actionMessage = t('set_type_message')
    }
  } else {
    state.isSupportKME = true

    const layoutFn = data.LayoutFn
    const hasFnCustomKeyMap = Boolean(layoutFn.KeyMap[kk])
    if ((hasFnCustomKeyMap || layoutFn.KeyMapModifier[kk]) && !factoryFn) {
      state.isShowMagnet = false
      state.isSupportKME = false
      state.actionMessage = t('set_type_message_fn')
    }
  }
  state.isShowMacro = state.isShowMagnet
}

function handleJingtaiV2Device({ kk, isFn, data, t }: ActionVisibilityContext, state: ActionVisibilityState) {
  if (isFn) {
    state.isShowMagnet = false
    state.isSupportKME = false
  } else {
    state.isSupportKME = true
  }
}

function handleRoYuanDevice({ isFn }: ActionVisibilityContext, state: ActionVisibilityState) {
  if (isFn) {
    state.isShowMagnet = false
    state.isSupportKME = false
  }
}

function handleDevicesOfSelfDev({ isFn }: ActionVisibilityContext, state: ActionVisibilityState) {
  if (isFn) {
    state.isShowMacro = false
    state.isSupportKME = false
  } else {
    state.isShowMacro = true
    state.isSupportKME = true
  }
}
